import React from 'react';
import Link from 'next/link';
import { Icon } from './Icon';
import * as Icons from 'lucide-react';

interface ButtonProps {
  label: string;
  href?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  iconLeft?: keyof typeof Icons;
  iconRight?: keyof typeof Icons;
  className?: string;
  onClick?: () => void;
  external?: boolean;
  disabled?: boolean;
  type?: "button" | "submit" | "reset" | undefined;
}

export const Button = ({
  label,
  href,
  variant = 'primary',
  iconLeft,
  iconRight,
  className = '',
  type = 'button',
  onClick,
  external = false,
  disabled = false,
}: ButtonProps) => {
  const baseStyles = `
    inline-flex items-center justify-center gap-2
    rounded-full uppercase font-normal text-sm
    transition-all duration-300 cursor-pointer whitespace-nowrap
  `;

  const variants = {
    primary: "px-3 lg:px-5 py-2 lg:py-3 bg-brand-primary text-white shadow-sm hover:shadow-lg hover:bg-brand-deep",
    secondary: "px-3 lg:px-5 py-2 lg:py-3 border border-white/30 bg-white/10 text-white backdrop-blur-sm shadow-sm hover:shadow-lg hover:bg-white/20 hover:border-white/50",
    outline: "px-3 lg:px-5 py-2 lg:py-3 border border-white text-white shadow-sm hover:shadow-lg hover:bg-white/10",
    ghost: "px-2 lg:px-3 py-1.5 lg:py-2 text-brand-primary hover:bg-brand-light"
  };

  const disabledStyles = disabled ? 'opacity-50 cursor-not-allowed' : '';

  const combinedClasses = `${baseStyles} ${variants[variant]} ${disabledStyles} ${className}`;

  const innerContent = (
    <>
      {iconLeft && <Icon name={iconLeft} size={18} color='#05A2F1' />}
      <span>{label}</span>
      {iconRight && <Icon name={iconRight} size={18} color='#05A2F1' />}
    </>
  );

  if (href) {
    return (
      <Link
        href={href}
        className={combinedClasses}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        aria-label={external ? `${label} (opens in new tab)` : undefined}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : undefined}
      >
        {innerContent}
      </Link>
    );
  }

  return (
    <button
      onClick={onClick}
      className={combinedClasses}
      type={type}
      disabled={disabled}
      aria-disabled={disabled || undefined}
    >
      {innerContent}
    </button>
  );
};
